/**
 * Org Invite Acceptance Screen — Phase 2B
 *
 * Route: /org/invite/:token
 * Deep link: duel://org/invite/<token>
 * Web link: https://douevenlift.com/join/<token>
 *
 * Flow:
 *   1. Read token from route param
 *   2. Fetch invite details (org name, role, status)
 *   3. If invalid/expired/revoked → show error state
 *   4. If unauthenticated → redirect to auth with return path
 *   5. If already a member → show "already a member" state
 *   6. If valid + authenticated → show Accept button
 *   7. On accept → createMembership → navigate to org roster
 *
 * Idempotency: acceptOrgInvite handles duplicate calls gracefully.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  Pressable,
  SafeAreaView,
  ActivityIndicator,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { getOrgInviteByToken, acceptOrgInvite } from '@/lib/orgInvites';
import { fetchOrganization } from '@/lib/organizations';
import { isSchoolComplianceRequired, hasCompletedMinorCompliance } from '@/lib/minorCompliance';
import { TYPO } from '@/constants/typography';
import type { OrgInvite } from '@/types/database';
import type { Organization } from '@/types/database';

type ScreenState =
  | 'loading'
  | 'invalid'
  | 'expired'
  | 'revoked'
  | 'auth_required'
  | 'ready'
  | 'already_member'
  | 'accepting'
  | 'accepted'
  | 'error';

const ROLE_LABELS: Record<string, string> = {
  member: 'Member',
  coach: 'Coach',
  admin: 'Admin',
};

export default function OrgInviteAcceptScreen() {
  const { token } = useLocalSearchParams<{ token: string }>();
  const router = useRouter();
  const { theme } = useTheme();
  const { session, profile } = useAuth();
  const c = theme.colors;

  const [state, setState] = useState<ScreenState>('loading');
  const [invite, setInvite] = useState<OrgInvite | null>(null);
  const [org, setOrg] = useState<Organization | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // ── Load invite on mount ─────────────────────────────────────────────────
  const loadInvite = useCallback(async () => {
    if (!token) {
      setState('invalid');
      return;
    }

    setState('loading');
    const fetchedInvite = await getOrgInviteByToken(token);

    if (!fetchedInvite) {
      setState('invalid');
      return;
    }

    if (fetchedInvite.status === 'revoked') {
      setState('revoked');
      return;
    }
    if (fetchedInvite.status === 'expired') {
      setState('expired');
      return;
    }
    if (fetchedInvite.status === 'accepted') {
      // Already accepted — treat as valid so user can navigate to org
      setInvite(fetchedInvite);
      const fetchedOrg = await fetchOrganization(fetchedInvite.org_id);
      setOrg(fetchedOrg);
      setState('already_member');
      return;
    }
    if (fetchedInvite.status !== 'pending') {
      setState('invalid');
      return;
    }

    setInvite(fetchedInvite);

    // Fetch org details for display
    const fetchedOrg = await fetchOrganization(fetchedInvite.org_id);
    setOrg(fetchedOrg);

    // Auth gate
    if (!session?.user) {
      setState('auth_required');
      return;
    }

    // Phase 2E: school compliance check — must happen before showing Accept
    if (fetchedOrg && isSchoolComplianceRequired(fetchedOrg) && session?.user) {
      // Check if the joining user has completed minor compliance
      const { data: profileData } = await import('@/lib/supabase').then(({ supabase }) =>
        supabase
          .from('profiles')
          .select('birth_year, minor_compliance_completed')
          .eq('id', session.user.id)
          .maybeSingle()
      );
      if (profileData && !hasCompletedMinorCompliance(profileData)) {
        // User hasn't completed minor compliance; route them through it first.
        // The returnTo param ensures they come back here after completing compliance.
        router.replace({
          pathname: '/(onboarding)/guardian-consent',
        } as any);
        return;
      }
    }

    setState('ready');
  }, [token, session]);

  useEffect(() => {
    loadInvite();
  }, [loadInvite]);

  // ── Accept handler ───────────────────────────────────────────────────────
  const handleAccept = async () => {
    if (!invite || !session?.user?.id) return;

    setState('accepting');
    try {
      const result = await acceptOrgInvite(invite.token, session.user.id);
      setState('accepted');

      // Short delay then navigate to roster
      setTimeout(() => {
        router.replace({
          pathname: '/org-roster',
          params: { orgId: result.orgId },
        } as any);
      }, 1200);
    } catch (err: any) {
      setErrorMessage(err?.message ?? 'Failed to accept invite. Please try again.');
      setState('error');
    }
  };

  // ── Auth redirect ────────────────────────────────────────────────────────
  const handleAuthRedirect = () => {
    // Navigate to auth; include return path so post-login redirects back here
    router.push({
      pathname: '/(auth)/signin',
      params: { returnTo: `/org/invite/${token}` },
    } as any);
  };

  const handleNavigateToOrg = () => {
    if (invite?.org_id) {
      router.replace({
        pathname: '/org-roster',
        params: { orgId: invite.org_id },
      } as any);
    } else {
      router.replace('/');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const styles = makeStyles(c);

  if (state === 'loading') {
    return (
      <SafeAreaView style={styles.centered}>
        <ActivityIndicator color={c.accent} size="large" />
        <Text style={styles.loadingText}>Loading invite…</Text>
      </SafeAreaView>
    );
  }

  if (state === 'invalid') {
    return (
      <SafeAreaView style={styles.centered}>
        <Text style={styles.errorIcon}>🔗</Text>
        <Text style={styles.heading}>Invalid Invite</Text>
        <Text style={styles.subText}>
          This invite link is invalid or no longer exists. Ask the org admin to send a new one.
        </Text>
        <Pressable style={styles.btnSecondary} onPress={() => router.replace('/')}>
          <Text style={styles.btnSecondaryText}>Go Home</Text>
        </Pressable>
      </SafeAreaView>
    );
  }

  if (state === 'expired') {
    return (
      <SafeAreaView style={styles.centered}>
        <Text style={styles.errorIcon}>⏰</Text>
        <Text style={styles.heading}>Invite Expired</Text>
        <Text style={styles.subText}>
          This invite link has expired (invites are valid for 7 days). Ask the org admin to send a new one.
        </Text>
        <Pressable style={styles.btnSecondary} onPress={() => router.replace('/')}>
          <Text style={styles.btnSecondaryText}>Go Home</Text>
        </Pressable>
      </SafeAreaView>
    );
  }

  if (state === 'revoked') {
    return (
      <SafeAreaView style={styles.centered}>
        <Text style={styles.errorIcon}>🚫</Text>
        <Text style={styles.heading}>Invite Revoked</Text>
        <Text style={styles.subText}>
          This invite has been revoked. Contact the org admin if you believe this is a mistake.
        </Text>
        <Pressable style={styles.btnSecondary} onPress={() => router.replace('/')}>
          <Text style={styles.btnSecondaryText}>Go Home</Text>
        </Pressable>
      </SafeAreaView>
    );
  }

  if (state === 'auth_required') {
    return (
      <SafeAreaView style={styles.centered}>
        <Text style={styles.errorIcon}>🔐</Text>
        <Text style={styles.heading}>Sign In to Accept</Text>
        <Text style={styles.subText}>
          You need to be signed in to accept this invite
          {org ? ` to join ${org.name}` : ''}.
        </Text>
        <Pressable style={styles.btnPrimary} onPress={handleAuthRedirect}>
          <Text style={styles.btnPrimaryText}>Sign In / Create Account</Text>
        </Pressable>
      </SafeAreaView>
    );
  }

  if (state === 'already_member') {
    return (
      <SafeAreaView style={styles.centered}>
        <Text style={styles.errorIcon}>✅</Text>
        <Text style={styles.heading}>Already a Member</Text>
        <Text style={styles.subText}>
          You're already a member of {org?.name ?? 'this organization'}.
        </Text>
        <Pressable style={styles.btnPrimary} onPress={handleNavigateToOrg}>
          <Text style={styles.btnPrimaryText}>Go to Roster</Text>
        </Pressable>
      </SafeAreaView>
    );
  }

  if (state === 'accepting') {
    return (
      <SafeAreaView style={styles.centered}>
        <ActivityIndicator color={c.accent} size="large" />
        <Text style={styles.loadingText}>Joining organization…</Text>
      </SafeAreaView>
    );
  }

  if (state === 'accepted') {
    return (
      <SafeAreaView style={styles.centered}>
        <Text style={styles.errorIcon}>🎉</Text>
        <Text style={styles.heading}>Welcome!</Text>
        <Text style={styles.subText}>
          You've joined {org?.name ?? 'the organization'} as {invite ? ROLE_LABELS[invite.role] ?? invite.role : 'a member'}.
        </Text>
      </SafeAreaView>
    );
  }

  if (state === 'error') {
    return (
      <SafeAreaView style={styles.centered}>
        <Text style={styles.errorIcon}>⚠️</Text>
        <Text style={styles.heading}>Something went wrong</Text>
        <Text style={styles.subText}>{errorMessage}</Text>
        <Pressable style={styles.btnPrimary} onPress={loadInvite}>
          <Text style={styles.btnPrimaryText}>Try Again</Text>
        </Pressable>
        <Pressable style={[styles.btnSecondary, { marginTop: 8 }]} onPress={() => router.replace('/')}>
          <Text style={styles.btnSecondaryText}>Go Home</Text>
        </Pressable>
      </SafeAreaView>
    );
  }

  // state === 'ready'
  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }}>
      <ScrollView contentContainerStyle={styles.content}>
        {/* Header */}
        <View style={styles.headerRow}>
          <Pressable onPress={() => router.back()} style={styles.backBtn}>
            <Text style={styles.backBtnText}>← Back</Text>
          </Pressable>
        </View>

        {/* Invite card */}
        <View style={styles.card}>
          <Text style={styles.inviteLabel}>You've been invited to join</Text>
          <Text style={styles.orgName}>{org?.name ?? 'an organization'}</Text>

          <View style={styles.roleBadge}>
            <Text style={styles.roleBadgeText}>
              {invite ? ROLE_LABELS[invite.role] ?? invite.role : 'Member'}
            </Text>
          </View>

          <Text style={styles.inviteDetail}>
            Invited by a team admin · Expires in 7 days
          </Text>
        </View>

        {/* Accept */}
        <Pressable
          style={styles.btnPrimary}
          onPress={handleAccept}
        >
          <Text style={styles.btnPrimaryText}>Accept Invite</Text>
        </Pressable>

        <Text style={styles.disclaimer}>
          By accepting, you'll join this organization and your name will be visible to org admins.
        </Text>

        <Pressable onPress={() => router.replace('/')}>
          <Text style={styles.declineText}>Decline</Text>
        </Pressable>
      </ScrollView>
    </SafeAreaView>
  );
}

function makeStyles(c: any) {
  return StyleSheet.create({
    centered: {
      flex: 1,
      backgroundColor: c.background,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 32,
    },
    content: {
      padding: 20,
      paddingBottom: 48,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 24,
    },
    backBtn: {
      padding: 4,
    },
    backBtnText: {
      color: c.accent,
      fontSize: 15,
      fontFamily: TYPO.body.regular,
    },
    errorIcon: {
      fontSize: 48,
      marginBottom: 16,
    },
    heading: {
      fontSize: 22,
      fontFamily: TYPO.heading.bold,
      color: c.text,
      textAlign: 'center',
      marginBottom: 12,
    },
    subText: {
      fontSize: 15,
      color: c.textSecondary,
      textAlign: 'center',
      lineHeight: 22,
      marginBottom: 24,
    },
    loadingText: {
      marginTop: 16,
      fontSize: 14,
      color: c.textSecondary,
      fontFamily: TYPO.body.regular,
    },
    card: {
      backgroundColor: c.surface,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: c.border,
      padding: 24,
      alignItems: 'center',
      marginBottom: 24,
    },
    inviteLabel: {
      fontSize: 13,
      color: c.textSecondary,
      fontFamily: TYPO.body.regular,
      marginBottom: 8,
    },
    orgName: {
      fontSize: 24,
      fontFamily: TYPO.heading.bold,
      color: c.text,
      textAlign: 'center',
      marginBottom: 12,
    },
    roleBadge: {
      backgroundColor: c.accent + '20',
      borderRadius: 4,
      paddingHorizontal: 12,
      paddingVertical: 4,
      marginBottom: 12,
    },
    roleBadgeText: {
      fontSize: 13,
      fontFamily: TYPO.body.semibold,
      color: c.accent,
    },
    inviteDetail: {
      fontSize: 12,
      color: c.textTertiary,
      textAlign: 'center',
    },
    btnPrimary: {
      backgroundColor: c.accent,
      borderRadius: 6,
      padding: 16,
      alignItems: 'center',
      marginBottom: 12,
    },
    btnPrimaryText: {
      color: c.accentForeground,
      fontSize: 16,
      fontFamily: TYPO.heading.bold,
    },
    btnSecondary: {
      borderRadius: 6,
      borderWidth: 1.5,
      borderColor: c.border,
      padding: 14,
      alignItems: 'center',
    },
    btnSecondaryText: {
      color: c.text,
      fontSize: 15,
      fontFamily: TYPO.body.regular,
    },
    disclaimer: {
      fontSize: 12,
      color: c.textTertiary,
      textAlign: 'center',
      lineHeight: 18,
      marginBottom: 16,
    },
    declineText: {
      color: c.textTertiary,
      fontSize: 14,
      textAlign: 'center',
      textDecorationLine: 'underline',
    },
  });
}
